import { useLayoutEffect, useRef } from "react";

interface CitySvgProps {
  active?: boolean;
  className?: string;
}

const C = {
  front: "#c8c8c8",
  side:  "#d5d5d5",
  top:   "#dddddd",
  ffl:   "#d8d8d8",
  sfl:   "#e0e0e0",
  win:   "#e5e5e5",
  col:   "#d0d0d0",
  tgrd:  "#e8e8e8",
  gnd:   "#bbbbbb",
  gnd2:  "#d8d8d8",
};

const W = {
  front: 1.8,
  side:  1.5,
  top:   1.2,
  ffl:   0.8,
  sfl:   0.65,
  win:   0.5,
  col:   0.7,
  tgrd:  0.55,
  gnd:   2.4,
  gnd2:  0.8,
};

const BOT = 492;
const DX = 42;
const DY = 21;
const FH = 20;

const BLDS = [
  [10, 70, 280],
  [76, 200, 368],
  [206, 272, 186],
  [278, 478, 38],
  [484, 564, 154],
  [570, 722, 238],
  [728, 796, 140],
  [802, 958, 296],
  [964, 1038, 406],
] as const;

interface P {
  d: string;
  cls: string;
  pl: number;
  px: number;
  pd: number;
}

function buildPaths(): P[] {
  const out: P[] = [];
  const add = (d: string, cls: keyof typeof C, delay: number, dur: number) => {
    out.push({ d, cls, pl: 4000, px: delay, pd: dur });
  };
  const line = (x1: number, y1: number, x2: number, y2: number, cls: keyof typeof C, delay: number, dur: number) => {
    out.push({ d: `M${x1},${y1} L${x2},${y2}`, cls, pl: 1200, px: delay, pd: dur });
  };

  line(0, BOT, 1080, BOT, "gnd", 0, 1.2);
  line(0, BOT + 13, 1080, BOT + 13, "gnd2", 0.05, 0.8);

  BLDS.forEach(([x1, x2, top], i) => {
    const w = x2 - x1;
    const h = BOT - top;
    const d0 = 0.12 + i * 0.05;
    const S = 1.8;

    add(`M${x1},${BOT} L${x1},${top} L${x2},${top} L${x2},${BOT}`, "front", d0, (h / 95 + 0.5) * S);

    let ffl = "";
    for (let y = top + FH; y < BOT - 2; y += FH) {
      ffl += `M${x1},${y} L${x2},${y} `;
    }
    if (ffl) add(ffl, "ffl", d0 + 0.2, 0.5 * S);

    const cols = w > 130 ? 3 : 2;
    const cw = w / cols;
    let cl = "";
    for (let c = 1; c < cols; c++) {
      cl += `M${x1 + c * cw},${top} L${x1 + c * cw},${BOT} `;
    }
    if (cl) add(cl, "col", d0 + 0.25, 0.4 * S);

    const wpad = cw * 0.14;
    const wh = FH - 7;
    let wp = "";
    for (let y = top + 3; y < BOT - FH; y += FH) {
      for (let c = 0; c < cols; c++) {
        const wx = x1 + c * cw + wpad;
        const ww = cw - wpad * 2;
        wp += `M${wx},${y} L${wx + ww},${y} L${wx + ww},${y + wh} L${wx},${y + wh} Z `;
      }
    }
    if (wp) add(wp, "win", d0 + 0.35, 0.8 * S);

    add(`M${x2},${BOT} L${x2 + DX},${BOT - DY} L${x2 + DX},${top - DY} L${x2},${top}`, "side", d0 + 0.04, (h / 95 + 0.4) * S);

    let sfl = "";
    for (let y = top + FH; y < BOT - 2; y += FH) {
      sfl += `M${x2},${y} L${x2 + DX},${y - DY} `;
    }
    if (sfl) add(sfl, "sfl", d0 + 0.28, 0.4 * S);
    add(`M${x2 + DX * 0.5},${top - DY * 0.5} L${x2 + DX * 0.5},${BOT - DY * 0.5}`, "sfl", d0 + 0.3, 0.35 * S);

    add(`M${x1},${top} L${x2},${top} L${x2 + DX},${top - DY} L${x1 + DX},${top - DY} Z`, "top", d0 + 0.08, 0.35 * S);

    let tg = "";
    for (let s = 1; s < 3; s++) {
      const lx = x1 + w * (s / 3);
      tg += `M${lx},${top} L${lx + DX},${top - DY} `;
    }
    if (tg) add(tg, "tgrd", d0 + 0.12, 0.3 * S);

    const eH = 13;
    const eW = Math.min(w * 0.28, 54);
    const eX = x1 + w * 0.18;
    const sdx = DX * 0.45;
    const sdy = DY * 0.45;
    add(`M${eX},${top} L${eX},${top - eH} L${eX + eW},${top - eH} L${eX + eW},${top}`, "top", d0 + 0.1, 0.2 * S);
    add(`M${eX},${top - eH} L${eX + eW},${top - eH} L${eX + eW + sdx},${top - eH - sdy} L${eX + sdx},${top - eH - sdy} Z`, "top", d0 + 0.12, 0.18 * S);

    if (w > 90) {
      const e2X = x2 - w * 0.38;
      const e2W = Math.min(w * 0.16, 32);
      add(`M${e2X},${top} L${e2X},${top - 9} L${e2X + e2W},${top - 9} L${e2X + e2W},${top}`, "top", d0 + 0.14, 0.18 * S);
      add(`M${e2X},${top - 9} L${e2X + e2W},${top - 9} L${e2X + e2W + sdx * 0.8},${top - 9 - sdy * 0.8} L${e2X + sdx * 0.8},${top - 9 - sdy * 0.8} Z`, "top", d0 + 0.15, 0.15 * S);
    }

    const enW = Math.min(50, w * 0.32);
    const enH = 32;
    const enX = x1 + (w - enW) / 2;
    add(`M${enX},${BOT} L${enX},${BOT - enH} L${enX + enW},${BOT - enH} L${enX + enW},${BOT}`, "front", d0 + 0.55, 0.3 * S);
    line(enX + enW / 2, BOT - enH, enX + enW / 2, BOT, "col", d0 + 0.65, 0.15 * S);
  });

  const [mx1, mx2, mt] = BLDS[3];
  const mcx = (mx1 + mx2) / 2;
  const phW = 82;
  const phH = 26;
  const phX = mcx - phW / 2;
  const sdx = DX * 0.5;
  const sdy = DY * 0.5;
  add(`M${phX},${mt} L${phX},${mt - phH} L${phX + phW},${mt - phH} L${phX + phW},${mt}`, "front", 0.25, 0.2);
  add(`M${phX + phW},${mt} L${phX + phW + sdx},${mt - sdy} L${phX + phW + sdx},${mt - phH - sdy} L${phX + phW},${mt - phH}`, "side", 0.27, 0.18);
  add(`M${phX},${mt - phH} L${phX + phW},${mt - phH} L${phX + phW + sdx},${mt - phH - sdy} L${phX + sdx},${mt - phH - sdy} Z`, "top", 0.29, 0.16);
  add(`M${mcx},${mt - phH} L${mcx},${mt - phH - 48}`, "front", 0.4, 0.2);

  return out;
}

const ALL_PATHS = buildPaths();

export default function CitySvg({ active = false, className = "" }: CitySvgProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const paths = svg.querySelectorAll<SVGGeometryElement>(".city-path");
    if (!active) {
      paths.forEach((p) => p.classList.remove("city-draw"));
      return;
    }

    // active=true: 少し遅延後に city-draw クラスを追加 → CSS transition が発火
    const id = setTimeout(() => {
      paths.forEach((p) => p.classList.add("city-draw"));
    }, 80);

    return () => clearTimeout(id);
  }, [active]);

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1080 540"
      preserveAspectRatio="xMidYMax meet"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ width: "100%", display: "block" }}
    >
      {ALL_PATHS.map((p, i) => (
        <path
          key={i}
          d={p.d}
          className="city-path"
          fill="none"
          stroke={C[p.cls as keyof typeof C]}
          strokeWidth={W[p.cls as keyof typeof W]}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            ["--pl" as string]: p.pl,
            ["--px" as string]: `${p.px}s`,
            ["--pd" as string]: `${p.pd}s`,
          }}
        />
      ))}
    </svg>
  );
}